/**
 * Notification Dropdown Component
 * Shows notifications in a dropdown menu with real-time updates
 */
import React, { useRef, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CheckCheck,
  Clock,
  Receipt,
  FileText,
  FileCheck,
  XCircle,
  AlertTriangle,
  AlertCircle,
  Shield,
  Info,
  MessageSquare,
  CheckCircle,
  Loader2,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { cn } from 'lib/utils';
import { useNotifications } from '../../contexts/NotificationContext';
import { useCompany } from '../../contexts/CompanyContext';
import { ROUTES } from '../../constants/routes';
import { getNotificationTypeConfig } from '../../services/notificationService';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import NotificationBadge from './NotificationBadge';

// Icon mapping
const ICON_MAP = {
  Receipt: Receipt,
  FileText: FileText,
  FileCheck: FileCheck,
  XCircle: XCircle,
  AlertTriangle: AlertTriangle,
  AlertCircle: AlertCircle,
  Shield: Shield,
  Info: Info,
  MessageSquare: MessageSquare,
  MessageCircle: MessageSquare,
  CheckCircle: CheckCircle,
  Clock: Clock,
  Bell: Bell,
};

const NotificationDropdown = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    isConnected,
    markAsRead,
    markAllAsRead,
    loadMore,
    hasMore,
  } = useNotifications();
  const { selectedCompanyId, selectedCompany, companies } = useCompany();

  const notificationListPath = (() => {
    const companyId =
      selectedCompanyId || selectedCompany?.id || companies?.[0]?.id;
    return companyId ? ROUTES.COMPANY_NOTIFICATIONS(companyId) : ROUTES.HOME;
  })();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    if (notification.actionUrl) {
      // Handle relative URLs
      if (notification.actionUrl.startsWith('/')) {
        navigate(notification.actionUrl);
      } else if (notification.actionUrl.startsWith('http')) {
        window.open(notification.actionUrl, '_blank');
      }
    }

    setIsOpen(false);
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: es,
      });
    } catch {
      return '';
    }
  };

  const getIcon = (type) => {
    const config = getNotificationTypeConfig(type);
    const IconComponent = ICON_MAP[config.icon] || Bell;
    return { IconComponent, config };
  };

  const recentNotifications = notifications.slice(0, 5);

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex h-9 w-9 items-center justify-center rounded-[var(--rely-radius-control)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] text-[var(--rely-text-muted)] transition-[background-color,border-color,color] duration-200 hover:bg-[var(--rely-surface-elevated)] hover:text-[var(--rely-text-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rely-color-brand-green)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--rely-surface-page)]"
        aria-label={t('notifications.title', 'Notificaciones')}
      >
        <Bell className="h-5 w-5" />
        <NotificationBadge count={unreadCount} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-[var(--rely-radius-panel)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] shadow-[var(--rely-shadow-panel)] sm:w-96">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--rely-border-subtle)] px-4 py-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[var(--rely-text-strong)]">
                {t('notifications.title', 'Notificaciones')}
              </h3>
              {/* Connection Status */}
              {isConnected ? (
                <Wifi className="h-3.5 w-3.5 text-[var(--success)]" />
              ) : (
                <WifiOff className="h-3.5 w-3.5 text-[var(--rely-text-muted)]" />
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead()}
                className="flex items-center gap-1 text-xs font-medium text-[var(--rely-text-strong)] transition-colors hover:text-[var(--rely-color-brand-green)]"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {t('notifications.markAllRead', 'Marcar todas como leídas')}
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[var(--rely-color-brand-green)]" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="mb-2 h-8 w-8 text-[var(--rely-text-muted)]" />
                <p className="text-sm text-[var(--rely-text-muted)]">
                  {t('notifications.empty', 'No hay notificaciones')}
                </p>
              </div>
            ) : (
              <>
                {recentNotifications.map((notification) => {
                  const { IconComponent, config } = getIcon(notification.type);

                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--rely-surface-elevated)]',
                        !notification.isRead &&
                          'bg-[var(--rely-surface-selected)]'
                      )}
                    >
                      {/* Icon */}
                      <div
                        className={cn(
                          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[var(--rely-radius-control)]',
                          config.bgColor
                        )}
                      >
                        <IconComponent
                          className={cn('h-4 w-4', config.color)}
                        />
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={cn(
                              'text-sm',
                              notification.isRead
                                ? 'text-[var(--rely-text-muted)]'
                                : 'font-medium text-[var(--rely-text-strong)]'
                            )}
                          >
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[var(--rely-color-brand-green)]" />
                          )}
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-xs text-[var(--rely-text-muted)]">
                          {notification.message}
                        </p>
                        <p className="mt-1 flex items-center gap-1 text-[10px] text-[var(--rely-text-muted)]">
                          <Clock className="h-3 w-3" />
                          {formatTime(notification.createdAt)}
                        </p>
                      </div>
                    </button>
                  );
                })}

                {/* Load More */}
                {hasMore && notifications.length > 5 && (
                  <button
                    onClick={loadMore}
                    disabled={isLoading}
                    className="flex w-full items-center justify-center gap-2 border-t border-[var(--rely-border-subtle)] py-3 text-xs font-medium text-[var(--rely-text-strong)] transition-colors hover:bg-[var(--rely-surface-elevated)]"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('notifications.loadMore', 'Cargar más')
                    )}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Footer - View All */}
          {notifications.length > 0 && (
            <Link
              to={notificationListPath}
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center border-t border-[var(--rely-border-subtle)] py-3 text-xs font-medium text-[var(--rely-text-strong)] transition-colors hover:bg-[var(--rely-surface-elevated)]"
            >
              {t('notifications.viewAll', 'Ver todas las notificaciones')}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
